/**
 * This file defines the recommendations3P() service.
 */
import type { AnyServiceWithDeps, DependenciesContext } from '../../core/types';
import {
	createAnalyticsEvent,
	fireErrorAnalytics,
	getCurrentCloudId,
	getCurrentProduct,
	type Product,
} from '../common/common';
import { fetchJSON } from '../common/fetchJSON';

export interface UserInfo {
	source: string;
	email: string;
	avatarUrl: string;
	displayName: string;
	atlassianAccountId?: string;
	thirdPartyUserId: string;
	collaborationStrength: number;
}

type Recommendation3PResult = {
	recommendations: UserInfo[];
};

export type Recommendations3PInput = {
	limit: number;
	targetCloudId?: string;
	targetProduct?: Product;
};

const dependencies = {
	fetchJSON,
	createAnalyticsEvent,
	fireErrorAnalytics,
	getCurrentCloudId,
	getCurrentProduct,
};
type Deps = typeof dependencies;

export const fetch3PInviteRecommendations = Object.assign(fetch3PInviteRecommendationsImpl, {
	dependencies,
}) satisfies AnyServiceWithDeps<Deps>;

export async function fetch3PInviteRecommendationsImpl(
	input: Recommendations3PInput,
	context: DependenciesContext<Deps>,
): Promise<UserInfo[]> {
	const deps = context.dependencies();
	const cloudId = input.targetCloudId ?? deps.getCurrentCloudId();
	const limit = input.limit;
	const product = input.targetProduct ?? deps.getCurrentProduct();
	try {
		const response = await deps.fetchJSON<Recommendation3PResult>({
			url: `/gateway/api/pi-gateway/frontend/api/1/recommend/invite-users?limit=${limit}&targetCloudId=${cloudId}&targetProduct=${product}`,
			method: 'GET',
		});
		return response.recommendations;
	} catch (error: any) {
		deps.fireErrorAnalytics({
			meta: {
				id: 'fetchInviteRecommendations3P',
				packageName: 'expansion-services',
				teamName: 'Groot',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		return [];
	}
}

export type MapToFeaturedUserInput = {
	userInfo: UserInfo[];
	tooltipRenderer: (user: UserInfo) => string;
};

const mapToFeaturedUserImpl = (input: MapToFeaturedUserInput) => {
	return input.userInfo.map((user, index) => ({
		email: user.email,
		name: user.displayName,
		avatarUrl: user.avatarUrl,
		source: user.source,
		tooltip: input.tooltipRenderer(user),
		id: user.atlassianAccountId ?? user.thirdPartyUserId,
	}));
};

export const mapToFeaturedUser = Object.assign(mapToFeaturedUserImpl, {
	dependencies: {},
}) satisfies AnyServiceWithDeps<{}>;
